import type { EstadoPaso, EstadoWebchat, ResultadoCheck, WebchatOperacion, WebchatPaso } from "@bo/shared";

/** Tono del punto de estado: lo pinta StatusDot, siempre con su etiqueta en texto. */
export type Tono = "ok" | "aviso" | "error" | "info" | "neutro";

export const ESTADO_WEBCHAT: Record<EstadoWebchat, { etiqueta: string; tono: Tono }> = {
  ACTIVO: { etiqueta: "Activo", tono: "ok" },
  SUSPENDIDO: { etiqueta: "Suspendido", tono: "aviso" },
  BAJA: { etiqueta: "De baja", tono: "neutro" },
  ALTA_EN_CURSO: { etiqueta: "Alta en curso", tono: "info" },
  ALTA_CON_ERROR: { etiqueta: "Alta con error", tono: "error" },
};

export const RESULTADO_CHECK: Record<ResultadoCheck, { etiqueta: string; tono: Tono }> = {
  OK: { etiqueta: "OK", tono: "ok" },
  AVISO: { etiqueta: "Aviso", tono: "aviso" },
  ERROR: { etiqueta: "Error", tono: "error" },
  OMITIDO: { etiqueta: "Omitido", tono: "neutro" },
};

export const ESTADO_PASO: Record<EstadoPaso, { etiqueta: string; tono: Tono }> = {
  PENDIENTE: { etiqueta: "Pendiente", tono: "neutro" },
  OK: { etiqueta: "Hecho", tono: "ok" },
  ERROR: { etiqueta: "Error", tono: "error" },
  OMITIDO: { etiqueta: "Omitido", tono: "neutro" },
};

export const ESTADO_OPERACION: Record<WebchatOperacion["estado"], { etiqueta: string; tono: Tono }> = {
  EN_CURSO: { etiqueta: "En curso", tono: "info" },
  COMPLETA: { etiqueta: "Completa", tono: "ok" },
  ERROR: { etiqueta: "Con error", tono: "error" },
  DESCARTADA: { etiqueta: "Descartada", tono: "neutro" },
};

export const NOMBRE_PASO: Record<WebchatPaso["paso"], { titulo: string; descripcion: string }> = {
  core: {
    titulo: "Canal en la base",
    descripcion: "CANAL, skill, ficha, subdominio, tema y atajos, en una sola transacción auditada.",
  },
  cf_regla: {
    titulo: "Regla de Cloudflare",
    descripcion: "El host entra en la regla que agrega la cabecera de origen (su valor nunca se muestra).",
  },
  cf_dns: {
    titulo: "Registro DNS",
    descripcion: "Registro A con proxy hacia el origen del webchat, con el comentario del canal.",
  },
  checklist: {
    titulo: "Checklist",
    descripcion: "Registro, regla, TLS, /api/tema, página y marca. Un aviso no frena el alta.",
  },
};

export const NOMBRE_CHECK: Record<string, string> = {
  cf_dns: "Registro DNS en Cloudflare",
  cf_regla: "Host en la regla de Cloudflare",
  tls: "Certificado TLS",
  api_tema: "GET /api/tema",
  pagina: "Página con el tema aplicado",
  marca: "Imágenes de marca (/marca/*)",
  idpagina: "URL de callback del bot",
};

/** Códigos de error de un paso, explicados para la persona (sin valores de terceros). */
export const AYUDA_CODIGO: Record<string, string> = {
  CF_REGISTRO_SIN_COMENTARIO:
    "El registro DNS no tiene el comentario webchat-ext:c<IDCANAL> (los webchats de antes). Ponelo en el panel de Cloudflare y reanudá.",
  CF_NOMBRE_OCUPADO:
    "Ya hay otro registro DNS con ese nombre: el host no queda en la regla de Cloudflare. Revisalo en el panel, borralo si no corresponde y reanudá.",
  CF_REGISTRO_AJENO:
    "El registro DNS de ese nombre no es de este webchat (otro tipo, otra IP, sin proxy o de otro canal): no se borra, y el host igual sale de la regla. Revisalo en el panel.",
  CF_SIN_REGISTRO: "El host no tiene registro DNS: no se agrega a la regla.",
  CF_REGLA_CAMBIO: "La regla de Cloudflare cambió mientras se editaba: no se tocó nada. Reanudá.",
  CF_REGLA_FORMA: "La expresión de la regla no tiene la forma esperada (http.host in {...}). Hay que revisarla en el panel.",
  CF_REGLA_LLENA: "La regla llegó a su largo máximo: hace falta intervenir a mano en Cloudflare.",
  CF_REGLA_VACIA: "Quitar este host dejaría la regla vacía; no se cambió nada.",
  CF_OCUPADO: "Otra persona estaba cambiando Cloudflare. Reanudá en unos segundos.",
  WEBCHAT_OCUPADO: "Había otra operación en curso sobre este webchat (un alta, una baja o la regla). Reanudá en unos segundos.",
  CF_ERROR: "Cloudflare respondió con un error. Reanudá en unos minutos.",
  WEBCHAT_CONFIG_MISSING: "Falta configurar Cloudflare o S3 en bo-api.",
  GRANT_MISSING: "Faltan permisos de base de datos para bo_app (sql/2026-10-14-webchats-grants.sql).",
  AUDITORIA_FALLO: "No se pudo registrar la auditoría y no se guardó nada. Reanudá.",
  ERROR_INTERNO: "Error interno del servidor. Reanudá; si se repite, avisá al equipo.",
};
